import { without } from 'es-toolkit';
import React, { useMemo } from 'react';

import { formatLumberGrade, LUMBER_GRADES, LumberGrade, LumberSpecies } from '@paragon/contract-library';
import { naturalSort } from '@paragon/utilities';

import { getAllowedGradesBySpecies } from '../../../../selectors/lumberOptionGroupSelectors';
import { makeGradeHumanReadable } from '../../../../util';
import { useAppSelector } from '../../../hooks';
import { LumberPropertySelectDialog } from './LumberPropertySelectDialog';
import { LumberSelectProps } from './LumberSelectProps';

const SORTED_GRADES = without(LUMBER_GRADES, 'Combo', 'Unknown').toSorted(naturalSort);

const emptyArray = [] as const;

type Props = LumberSelectProps<LumberGrade> & {
  readonly disabled: boolean;
  readonly selectedSpecies: readonly LumberSpecies[];
};

export function LumberGradeSelect(props: Props) {
  const allowedGradesBySpecies = useAppSelector(getAllowedGradesBySpecies);

  const lumberGradeOptions = useMemo(() => {
    if (allowedGradesBySpecies == null) {
      return null;
    }

    const selectedGradesSet = new Set(props.value);

    return SORTED_GRADES.filter(
      (grade) =>
        // A valid grade option must be valid for all selected species
        props.selectedSpecies.every((species) => allowedGradesBySpecies[species]?.has(grade)) ||
        // We also include selected grades even if they are not valid, so that the user can deselect them
        selectedGradesSet.has(grade),
    );
  }, [props.selectedSpecies, props.value, allowedGradesBySpecies]);

  return (
    <LumberPropertySelectDialog
      type="grade"
      values={lumberGradeOptions ?? emptyArray}
      selectedValues={props.value}
      errorMessage={lumberGradeOptions?.length === 0 ? 'The selected species do not have any grades in common.' : null}
      onSelectedValuesChange={props.onChange}
      formatSingleValue={makeGradeHumanReadable}
      // This control is not used for combo species, so we can safely pass [] as tangible species
      formatMultipleValues={(grades) =>
        grades
          .map((grade) => formatLumberGrade(grade, []))
          .toSorted(naturalSort)
          .join(', ')
      }
      disabled={props.disabled}
    />
  );
}
